import { useState } from "react";

import { usePostHog } from "posthog-js/react";

import { ClientAppEvent } from "constants/index";
import { useCarrierQuoteFiltersContext } from "context/carrierQuoteFiltersContext";

type QuoteFilterIdentifier =
  | "quote_status"
  | "equipment_type"
  | "service_type"
  | "shipment_type"
  | "origin"
  | "destination"
  | "pickup_date"
  | "delivery_date"
  | "shipment_status"
  | "shipment_name"
  | "sort_by";

export type QuoteFilterSurface = "dashboard" | "shipment_detail_sidebar";

interface Props {
  surface: QuoteFilterSurface;
}

export function useCarrierQuoteFilterHandlers(props: Props) {
  const { surface } = props;
  const { filters, dispatch } = useCarrierQuoteFiltersContext();
  const [filterClearCount, setFilterClearCount] = useState(0);
  const posthog = usePostHog();

  const captureQuoteFilterSelected = (
    filterIdentifier: QuoteFilterIdentifier,
  ) => {
    posthog?.capture(ClientAppEvent.CARRIER_MY_QUOTES_FILTER_SELECTED, {
      filter_identifier: filterIdentifier,
      surface,
    });
  };

  const handleShipmentNameChange = (value: string) => {
    dispatch({ type: "setShipmentName", shipmentName: value });
    captureQuoteFilterSelected("shipment_name");
  };

  const handleQuoteStatusChange = (value: string[]) => {
    dispatch({ type: "setQuoteStatuses", statuses: value });
    captureQuoteFilterSelected("quote_status");
  };

  const handleSortByChange = (value: string) => {
    dispatch({ type: "setSortBy", sortBy: value });
    captureQuoteFilterSelected("sort_by");
  };

  const handleEquipmentTypesChange = (value: string[]) => {
    dispatch({ type: "setEquipmentTypes", equipmentTypes: value });
    captureQuoteFilterSelected("equipment_type");
  };

  const handleServiceTypesChange = (value: string[]) => {
    dispatch({ type: "setServiceTypes", serviceTypes: value });
    captureQuoteFilterSelected("service_type");
  };

  const handleShipmentTypeChange = (value: string) => {
    dispatch({
      type: "setShipmentTypes",
      shipmentTypes: [value] as QuoteShipmentTypeFilterValue[],
    });
    captureQuoteFilterSelected("shipment_type");
  };

  const handleOriginProvincesChange = (value: string[]) => {
    dispatch({ type: "setOriginProvinces", provinces: value });
    captureQuoteFilterSelected("origin");
  };

  const handleDestinationProvincesChange = (value: string[]) => {
    dispatch({ type: "setDestinationProvinces", provinces: value });
    captureQuoteFilterSelected("destination");
  };

  const handlePickupDateAfterChange = (value: string) => {
    dispatch({ type: "setPickupDateAfter", date: value });
    captureQuoteFilterSelected("pickup_date");
  };

  const handlePickupDateBeforeChange = (value: string) => {
    dispatch({ type: "setPickupDateBefore", date: value });
    captureQuoteFilterSelected("pickup_date");
  };

  const handleDeliveryDateAfterChange = (value: string) => {
    dispatch({ type: "setDeliveryDateAfter", date: value });
    captureQuoteFilterSelected("delivery_date");
  };

  const handleDeliveryDateBeforeChange = (value: string) => {
    dispatch({ type: "setDeliveryDateBefore", date: value });
    captureQuoteFilterSelected("delivery_date");
  };

  const handleShipmentStatusChange = (value: string[]) => {
    dispatch({ type: "setShipmentStatuses", statuses: value });
    captureQuoteFilterSelected("shipment_status");
  };

  const handleClearFilters = () => {
    dispatch({ type: "clearQuoteFilters" });
    setFilterClearCount((prev) => prev + 1);
  };

  return {
    filters,
    filterClearCount,
    handleShipmentNameChange,
    handleQuoteStatusChange,
    handleSortByChange,
    handleEquipmentTypesChange,
    handleServiceTypesChange,
    handleShipmentTypeChange,
    handleOriginProvincesChange,
    handleDestinationProvincesChange,
    handlePickupDateAfterChange,
    handlePickupDateBeforeChange,
    handleDeliveryDateAfterChange,
    handleDeliveryDateBeforeChange,
    handleShipmentStatusChange,
    handleClearFilters,
  };
}
